'use client'

/**
 * Background Container
 * Reusable wrapper component for section backgrounds
 * Supports color, gradient, image, and video backgrounds
 */

import React from 'react'

import type { BackgroundValue } from '@/types/element-schema.type'

export interface BackgroundContainerProps {
  /** Background configuration */
  background?: BackgroundValue
  /** Children to render inside */
  children: React.ReactNode
  /** Additional CSS classes */
  className?: string
  /** Inline styles to merge */
  style?: React.CSSProperties
}

/**
 * Get background style based on configuration
 */
function getBackgroundStyle(background?: BackgroundValue): React.CSSProperties {
  if (!background || background.type === 'none') {
    return {}
  }

  const style: React.CSSProperties = {}

  switch (background.type) {
    case 'color':
      if (background.color) {
        style.backgroundColor = background.color
      }
      break

    case 'gradient':
      if (background.gradient?.colors?.length) {
        const { colors, direction, angle } = background.gradient
        const gradientDirection = direction || `${angle || 90}deg`
        style.background = `linear-gradient(${gradientDirection}, ${colors.join(', ')})`
      }
      break

    case 'image':
      if (background.image?.src) {
        style.backgroundImage = `url(${background.image.src})`
        style.backgroundPosition = background.image.position || 'center'
        style.backgroundSize = background.image.size || 'cover'
        style.backgroundRepeat = background.image.repeat ? 'repeat' : 'no-repeat'
      }
      break

    case 'video':
      // Video backgrounds are handled differently - see render
      break
  }

  return style
}

/**
 * Background Container Component
 */
export function BackgroundContainer({ background, children, className = '', style }: BackgroundContainerProps) {
  const bgStyle = getBackgroundStyle(background)
  const mergedStyle = { ...bgStyle, ...style }

  // No background or color/gradient/image
  if (!background || background.type === 'none' || background.type !== 'video') {
    // Check for overlay
    const hasOverlay = background?.overlay?.color && background?.overlay?.opacity
    const overlayColor = background?.overlay?.color
    const overlayOpacity = background?.overlay?.opacity

    return (
      <div className={`relative ${className}`} style={mergedStyle}>
        {/* Overlay layer */}
        {hasOverlay && overlayColor && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundColor: overlayColor,
              opacity: overlayOpacity ?? 0.5,
            }}
          />
        )}
        {/* Content */}
        <div className="relative z-10">{children}</div>
      </div>
    )
  }

  // Video background
  if (background.type === 'video' && background.video?.src) {
    const hasOverlay = background.overlay?.color && background.overlay?.opacity
    const overlayColor = background.overlay?.color
    const overlayOpacity = background.overlay?.opacity

    return (
      <div className={`relative overflow-hidden ${className}`} style={mergedStyle}>
        {/* Video element */}
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src={background.video.src}
          poster={background.video.poster}
          autoPlay={background.video.autoplay ?? true}
          loop={background.video.loop ?? true}
          muted={background.video.muted ?? true}
          playsInline
        />
        {/* Overlay layer */}
        {hasOverlay && overlayColor && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundColor: overlayColor,
              opacity: overlayOpacity ?? 0.5,
            }}
          />
        )}
        {/* Content */}
        <div className="relative z-10">{children}</div>
      </div>
    )
  }

  // Fallback
  return <div className={className}>{children}</div>
}

export default BackgroundContainer
